import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useApp } from './AppContext';
import translations from '../i18n/translations';
import extraTranslations from '../i18n/extra_translations';

const RuntimeContext = createContext(null);

// ── RTL languages ──────────────────────────────────────────────────────────
const RTL_LANGS = new Set(['ar']);

// ── Deep merge utility (ready for extra_translations when file is added) ───
function deepMerge(target, source) {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}


const allLangs = new Set([...Object.keys(translations), ...Object.keys(extraTranslations)]);
const mergedTranslations = {};
for (const lang of allLangs) {
  mergedTranslations[lang] = deepMerge(translations[lang] || {}, extraTranslations[lang] || {});
}

// ── Translate helper (flat dotted keys in nested objects) ──────────────────
function resolve(dict, key) {
  if (!dict) return undefined;
  const parts = key.split('.');
  let cur = dict;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] ?? ''));
}

export const RuntimeProvider = ({ children }) => {
  const { settings, updateSettings, store } = useApp();

  // English is the default interface language: merchants installing this app
  // are worldwide, and until one picks a language (LanguagePickerModal, shown
  // on first run while ui_language is absent) the app has nothing better to
  // go on. A merchant who has chosen — including everyone already on Italian
  // — has ui_language stored and is unaffected.
  //
  // This is the INTERFACE language only. The language the AI writes product
  // content in is settings.default_language, which is deliberately separate
  // and untouched: an Italian merchant wants an English UI no more than they
  // want English product descriptions on their Italian storefront.
  const language    = settings?.ui_language              || 'en';
  const theme       = settings?.appearance?.theme        || 'light';
  const density     = settings?.appearance?.density      || 'comfortable';
  const sidebarMode = settings?.appearance?.sidebar      || 'expanded';

  // ── Translation function ────────────────────────────────────────────────
  const t = useCallback((key, fallback, vars) => {
    // Both fallbacks are English. The second one matters most: it is what a
    // merchant sees for a key their language has not translated yet, and
    // showing them Italian there is worse than showing them English.
    const dict = mergedTranslations[language] || mergedTranslations.en;
    const str = (
      resolve(dict, key) ||
      resolve(mergedTranslations.en, key) ||
      fallback ||
      key
    );
    return vars ? interpolate(str, vars) : str;
  }, [language]);

  // ── Apply theme (Tailwind darkMode: ["class"]) ──────────────────────────
  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [theme]);

  // ── Apply density ───────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  // ── Apply language direction & lang attr ───────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', RTL_LANGS.has(language) ? 'rtl' : 'ltr');
  }, [language]);

  // ── Setters — instant save + context update ────────────────────────────
  const _saveAppearance = useCallback((key, value) => {
    if (!store?.id) return;
    const next = {
      ...(settings || {}),
      appearance: { ...(settings?.appearance || {}), [key]: value },
    };
    updateSettings(store.id, next).catch(() => {});
  }, [settings, store?.id, updateSettings]);

  const setTheme       = useCallback((v) => _saveAppearance('theme', v),   [_saveAppearance]);
  const setDensity     = useCallback((v) => _saveAppearance('density', v), [_saveAppearance]);
  const setSidebarMode = useCallback((v) => _saveAppearance('sidebar', v), [_saveAppearance]);

  const setLanguage = useCallback((lang) => {
    if (!store?.id) return;
    updateSettings(store.id, { ...(settings || {}), ui_language: lang }).catch(() => {});
  }, [settings, store?.id, updateSettings]);

  const value = useMemo(() => ({
    t,
    language, setLanguage,
    theme, setTheme,
    density, setDensity,
    sidebarMode, setSidebarMode,
  }), [t, language, setLanguage, theme, setTheme, density, setDensity, sidebarMode, setSidebarMode]);

  return (
    <RuntimeContext.Provider value={value}>
      {children}
    </RuntimeContext.Provider>
  );
};

export const useRuntime = () => {
  const ctx = useContext(RuntimeContext);
  if (!ctx) throw new Error('useRuntime must be used within RuntimeProvider');
  return ctx;
};
